'use client';

import { useMemo, useState } from 'react';
import Link from 'next/link';
import styles from './MeasurementPlan.module.scss';
import { Check, Copy, Download, UserCheck } from 'lucide-react';
import {
  buildConfluenceMarkdown,
  downloadPlanMarkdown,
} from '@/src/lib/measurementPlan/confluenceTemplate';
import {
  metricCategorySchema,
  type MeasurementPlan,
  type Metric,
} from '@/src/lib/measurementPlan/schema';

interface FullPlanEditorProps {
  initialPlan: MeasurementPlan;
  isEn: boolean;
  lang: 'fr' | 'en';
  onTrack: (
    type:
      | 'mvp_plan_confluence_copied'
      | 'mvp_plan_md_downloaded'
      | 'mvp_plan_human_review_requested'
  ) => void;
}

export function FullPlanEditor({ initialPlan, isEn, lang, onTrack }: FullPlanEditorProps) {
  const [plan, setPlan] = useState<MeasurementPlan>(initialPlan);
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied'>('idle');
  const [mdStatus, setMdStatus] = useState<'idle' | 'downloaded'>('idle');

  const markdown = useMemo(() => buildConfluenceMarkdown(plan, lang), [plan, lang]);

  function updateField<K extends keyof MeasurementPlan>(field: K, value: MeasurementPlan[K]) {
    setPlan((prev) => ({ ...prev, [field]: value }));
  }

  function updateStringList(
    field:
      | 'assumptions'
      | 'scope'
      | 'nonScope'
      | 'instrumentationPlan'
      | 'acceptanceCriteria'
      | 'handoverCriteria'
      | 'missingInformation',
    index: number,
    value: string
  ) {
    setPlan((prev) => ({
      ...prev,
      [field]: prev[field].map((item, i) => (i === index ? value : item)),
    }));
  }

  function addStringListItem(
    field:
      | 'assumptions'
      | 'scope'
      | 'nonScope'
      | 'instrumentationPlan'
      | 'acceptanceCriteria'
      | 'handoverCriteria'
      | 'missingInformation'
  ) {
    setPlan((prev) => ({ ...prev, [field]: [...prev[field], ''] }));
  }

  function removeStringListItem(
    field:
      | 'assumptions'
      | 'scope'
      | 'nonScope'
      | 'instrumentationPlan'
      | 'acceptanceCriteria'
      | 'handoverCriteria'
      | 'missingInformation',
    index: number
  ) {
    setPlan((prev) => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index),
    }));
  }

  function updateMetric(index: number, field: keyof Metric, value: string) {
    setPlan((prev) => ({
      ...prev,
      metrics: prev.metrics.map((m, i) => (i === index ? { ...m, [field]: value } : m)),
    }));
  }

  function updateMetricCategory(index: number, value: string) {
    const parsed = metricCategorySchema.safeParse(value);
    if (parsed.success) {
      updateMetric(index, 'category', parsed.data);
    }
  }

  function updateDebt(
    type: 'technicalDebt' | 'businessDebt',
    index: number,
    field: 'type' | 'description' | 'impact' | 'mitigation',
    value: string
  ) {
    setPlan((prev) => ({
      ...prev,
      [type]: prev[type].map((item, i) => (i === index ? { ...item, [field]: value } : item)),
    }));
  }

  function updateRule(index: number, field: 'condition' | 'action', value: string) {
    setPlan((prev) => ({
      ...prev,
      continueCorrectPivotStopRules: prev.continueCorrectPivotStopRules.map((rule, i) =>
        i === index ? { ...rule, [field]: value } : rule
      ),
    }));
  }

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(markdown);
      setCopyStatus('copied');
      onTrack('mvp_plan_confluence_copied');
      setTimeout(() => setCopyStatus('idle'), 2500);
    } catch {
      // clipboard may be unavailable
    }
  }

  function handleDownload() {
    downloadPlanMarkdown(plan, lang);
    setMdStatus('downloaded');
    onTrack('mvp_plan_md_downloaded');
    setTimeout(() => setMdStatus('idle'), 2500);
  }

  function handleHumanReview() {
    onTrack('mvp_plan_human_review_requested');
  }

  const labels = {
    titleLabel: isEn ? 'Plan title' : 'Titre du plan',
    executiveSummaryLabel: isEn ? 'Executive summary' : 'Résumé exécutif',
    businessObjectiveLabel: isEn ? 'Business objective' : 'Objectif métier',
    northStarMetricLabel: isEn ? 'North Star metric' : 'Métrique North Star',
    baselineLabel: isEn ? 'Baseline' : 'Référence',
    assumptionsLabel: isEn ? 'Assumptions' : 'Hypothèses',
    scopeLabel: isEn ? 'Scope' : 'Périmètre',
    nonScopeLabel: isEn ? 'Out of scope' : 'Hors périmètre',
    metricsLabel: isEn ? 'Metrics' : 'Métriques',
    instrumentationLabel: isEn ? 'Instrumentation plan' : "Plan d'instrumentation",
    acceptanceLabel: isEn ? 'Acceptance criteria' : "Critères d'acceptation",
    technicalDebtLabel: isEn ? 'Technical debt' : 'Dette technique',
    businessDebtLabel: isEn ? 'Business debt' : 'Dette métier',
    aiEvaluationLabel: isEn ? 'AI evaluation' : "Évaluation de l'IA",
    rulesLabel: isEn
      ? 'Continue / Correct / Pivot / Stop rules'
      : 'Règles Continuer / Corriger / Pivoter / Arrêter',
    handoverLabel: isEn ? 'Handover criteria' : 'Critères de transfert',
    missingLabel: isEn ? 'Missing information' : 'Informations manquantes',
    addItem: isEn ? 'Add item' : 'Ajouter un élément',
    removeItem: isEn ? 'Remove' : 'Supprimer',
    copyConfluence: isEn ? 'Copy for Confluence' : 'Copier pour Confluence',
    copied: isEn ? 'Copied!' : 'Copié !',
    downloadMd: isEn ? 'Download .md' : 'Télécharger .md',
    downloaded: isEn ? 'Downloaded!' : 'Téléchargé !',
    humanReview: isEn ? 'Have Averiane review it' : 'Faire relire par Averiane',
  };

  const metricHeaders: { key: keyof Metric; label: string }[] = [
    { key: 'name', label: isEn ? 'Name' : 'Nom' },
    { key: 'category', label: isEn ? 'Category' : 'Catégorie' },
    { key: 'reason', label: isEn ? 'Reason' : 'Raison' },
    { key: 'formula', label: isEn ? 'Formula' : 'Formule' },
    { key: 'unit', label: isEn ? 'Unit' : 'Unité' },
    { key: 'baseline', label: isEn ? 'Baseline' : 'Référence' },
    { key: 'target', label: isEn ? 'Target' : 'Cible' },
    { key: 'dataSource', label: isEn ? 'Data source' : 'Source' },
    { key: 'cadence', label: isEn ? 'Cadence' : 'Cadence' },
    { key: 'owner', label: isEn ? 'Owner' : 'Responsable' },
    { key: 'decisionThreshold', label: isEn ? 'Threshold' : 'Seuil' },
    { key: 'pairedGuardrail', label: isEn ? 'Guardrail' : 'Garde-fou' },
  ];

  function renderEditableList(
    field:
      | 'assumptions'
      | 'scope'
      | 'nonScope'
      | 'instrumentationPlan'
      | 'acceptanceCriteria'
      | 'handoverCriteria'
      | 'missingInformation',
    label: string
  ) {
    return (
      <div className={styles.planSection}>
        <h3 className={styles.planSectionTitle}>{label}</h3>
        <ul className={styles.planList}>
          {plan[field].map((item, idx) => (
            <li key={idx} className={styles.planListItem}>
              <span className={styles.planListBullet} />
              <input
                type="text"
                value={item}
                onChange={(e) => updateStringList(field, idx, e.target.value)}
                className={styles.planListInput}
                aria-label={`${label} ${idx + 1}`}
              />
              <button
                type="button"
                onClick={() => removeStringListItem(field, idx)}
                className={styles.retryButton}
                aria-label={`${labels.removeItem} ${idx + 1}`}
              >
                {labels.removeItem}
              </button>
            </li>
          ))}
        </ul>
        <button
          type="button"
          onClick={() => addStringListItem(field)}
          className={styles.actionButton}
        >
          {labels.addItem}
        </button>
      </div>
    );
  }

  return (
    <div className={styles.fullPlanEditor} data-testid="full-plan-editor">
      <div className={styles.planHeader}>
        <input
          type="text"
          value={plan.title}
          onChange={(e) => updateField('title', e.target.value)}
          className={styles.planTitleInput}
          aria-label={labels.titleLabel}
        />
        <textarea
          value={plan.executiveSummary}
          onChange={(e) => updateField('executiveSummary', e.target.value)}
          className={styles.planSummaryTextarea}
          aria-label={labels.executiveSummaryLabel}
        />
      </div>

      <div className={styles.planSection}>
        <h3 className={styles.planSectionTitle}>{labels.businessObjectiveLabel}</h3>
        <textarea
          value={plan.businessObjective}
          onChange={(e) => updateField('businessObjective', e.target.value)}
          className={styles.planTextarea}
          aria-label={labels.businessObjectiveLabel}
        />
      </div>

      <div className={styles.planSection}>
        <h3 className={styles.planSectionTitle}>{labels.northStarMetricLabel}</h3>
        <input
          type="text"
          value={plan.northStarMetric}
          onChange={(e) => updateField('northStarMetric', e.target.value)}
          className={styles.planListInput}
          aria-label={labels.northStarMetricLabel}
        />
      </div>

      <div className={styles.planSection}>
        <h3 className={styles.planSectionTitle}>{labels.baselineLabel}</h3>
        <textarea
          value={plan.baseline}
          onChange={(e) => updateField('baseline', e.target.value)}
          className={styles.planTextarea}
          aria-label={labels.baselineLabel}
        />
      </div>

      {renderEditableList('assumptions', labels.assumptionsLabel)}
      {renderEditableList('scope', labels.scopeLabel)}
      {renderEditableList('nonScope', labels.nonScopeLabel)}

      <div className={styles.planSection}>
        <h3 className={styles.planSectionTitle}>{labels.metricsLabel}</h3>
        <div className={styles.metricsTableWrapper}>
          <table className={styles.metricsTable}>
            <thead>
              <tr>
                {metricHeaders.map((h) => (
                  <th key={h.key}>{h.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {plan.metrics.map((metric, idx) => (
                <tr key={idx}>
                  {metricHeaders.map((h) => {
                    const value = metric[h.key];
                    if (h.key === 'category') {
                      return (
                        <td key={h.key}>
                          <select
                            value={value}
                            onChange={(e) => updateMetricCategory(idx, e.target.value)}
                            aria-label={`${labels.metricsLabel} ${idx + 1} ${h.label}`}
                          >
                            {metricCategorySchema.options.map((option) => (
                              <option key={option} value={option}>
                                {option}
                              </option>
                            ))}
                          </select>
                        </td>
                      );
                    }
                    return (
                      <td key={h.key}>
                        {h.key === 'reason' || h.key === 'formula' ? (
                          <textarea
                            value={value}
                            onChange={(e) => updateMetric(idx, h.key, e.target.value)}
                            aria-label={`${labels.metricsLabel} ${idx + 1} ${h.label}`}
                          />
                        ) : (
                          <input
                            type="text"
                            value={value}
                            onChange={(e) => updateMetric(idx, h.key, e.target.value)}
                            aria-label={`${labels.metricsLabel} ${idx + 1} ${h.label}`}
                          />
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {renderEditableList('instrumentationPlan', labels.instrumentationLabel)}
      {renderEditableList('acceptanceCriteria', labels.acceptanceLabel)}

      <div className={styles.planSection}>
        <h3 className={styles.planSectionTitle}>{labels.technicalDebtLabel}</h3>
        {plan.technicalDebt.map((item, idx) => (
          <div key={idx} className={styles.debtCard}>
            <div className={styles.debtField}>
              <span className={styles.debtLabel}>{isEn ? 'Type' : 'Type'}</span>
              <input
                type="text"
                value={item.type}
                onChange={(e) => updateDebt('technicalDebt', idx, 'type', e.target.value)}
                className={styles.planListInput}
              />
            </div>
            <div className={styles.debtField}>
              <span className={styles.debtLabel}>{isEn ? 'Description' : 'Description'}</span>
              <input
                type="text"
                value={item.description}
                onChange={(e) => updateDebt('technicalDebt', idx, 'description', e.target.value)}
                className={styles.planListInput}
              />
            </div>
            <div className={styles.debtField}>
              <span className={styles.debtLabel}>{isEn ? 'Impact' : 'Impact'}</span>
              <input
                type="text"
                value={item.impact}
                onChange={(e) => updateDebt('technicalDebt', idx, 'impact', e.target.value)}
                className={styles.planListInput}
              />
            </div>
            <div className={styles.debtField}>
              <span className={styles.debtLabel}>{isEn ? 'Mitigation' : 'Atténuation'}</span>
              <input
                type="text"
                value={item.mitigation}
                onChange={(e) => updateDebt('technicalDebt', idx, 'mitigation', e.target.value)}
                className={styles.planListInput}
              />
            </div>
          </div>
        ))}
      </div>

      <div className={styles.planSection}>
        <h3 className={styles.planSectionTitle}>{labels.businessDebtLabel}</h3>
        {plan.businessDebt.map((item, idx) => (
          <div key={idx} className={styles.debtCard}>
            <div className={styles.debtField}>
              <span className={styles.debtLabel}>{isEn ? 'Type' : 'Type'}</span>
              <input
                type="text"
                value={item.type}
                onChange={(e) => updateDebt('businessDebt', idx, 'type', e.target.value)}
                className={styles.planListInput}
              />
            </div>
            <div className={styles.debtField}>
              <span className={styles.debtLabel}>{isEn ? 'Description' : 'Description'}</span>
              <input
                type="text"
                value={item.description}
                onChange={(e) => updateDebt('businessDebt', idx, 'description', e.target.value)}
                className={styles.planListInput}
              />
            </div>
            <div className={styles.debtField}>
              <span className={styles.debtLabel}>{isEn ? 'Impact' : 'Impact'}</span>
              <input
                type="text"
                value={item.impact}
                onChange={(e) => updateDebt('businessDebt', idx, 'impact', e.target.value)}
                className={styles.planListInput}
              />
            </div>
            <div className={styles.debtField}>
              <span className={styles.debtLabel}>{isEn ? 'Mitigation' : 'Atténuation'}</span>
              <input
                type="text"
                value={item.mitigation}
                onChange={(e) => updateDebt('businessDebt', idx, 'mitigation', e.target.value)}
                className={styles.planListInput}
              />
            </div>
          </div>
        ))}
      </div>

      <div className={styles.planSection}>
        <h3 className={styles.planSectionTitle}>{labels.aiEvaluationLabel}</h3>
        <div className={styles.debtCard}>
          <label className={styles.debtField}>
            <span className={styles.debtLabel}>{isEn ? 'AI relevant' : 'IA concernée'}</span>
            <input
              type="checkbox"
              checked={plan.aiEvaluation.isAiRelevant}
              onChange={(e) =>
                setPlan((prev) => ({
                  ...prev,
                  aiEvaluation: { ...prev.aiEvaluation, isAiRelevant: e.target.checked },
                }))
              }
            />
          </label>
          <div className={styles.debtField}>
            <span className={styles.debtLabel}>{isEn ? 'Measures' : 'Mesures'}</span>
            {plan.aiEvaluation.measures.map((measure, idx) => (
              <div key={idx} className={styles.planListItem}>
                <span className={styles.planListBullet} />
                <input
                  type="text"
                  value={measure}
                  onChange={(e) =>
                    setPlan((prev) => ({
                      ...prev,
                      aiEvaluation: {
                        ...prev.aiEvaluation,
                        measures: prev.aiEvaluation.measures.map((m, i) =>
                          i === idx ? e.target.value : m
                        ),
                      },
                    }))
                  }
                  className={styles.planListInput}
                />
              </div>
            ))}
            <button
              type="button"
              onClick={() =>
                setPlan((prev) => ({
                  ...prev,
                  aiEvaluation: {
                    ...prev.aiEvaluation,
                    measures: [...prev.aiEvaluation.measures, ''],
                  },
                }))
              }
              className={styles.actionButton}
            >
              {labels.addItem}
            </button>
          </div>
          <div className={styles.debtField}>
            <span className={styles.debtLabel}>
              {isEn ? 'Risk summary' : 'Synthèse des risques'}
            </span>
            <textarea
              value={plan.aiEvaluation.riskSummary}
              onChange={(e) =>
                setPlan((prev) => ({
                  ...prev,
                  aiEvaluation: { ...prev.aiEvaluation, riskSummary: e.target.value },
                }))
              }
              className={styles.planTextarea}
            />
          </div>
        </div>
      </div>

      <div className={styles.planSection}>
        <h3 className={styles.planSectionTitle}>{labels.rulesLabel}</h3>
        {plan.continueCorrectPivotStopRules.map((rule, idx) => (
          <div key={idx} className={styles.debtCard}>
            <div className={styles.debtField}>
              <span className={styles.debtLabel}>{isEn ? 'Condition' : 'Condition'}</span>
              <input
                type="text"
                value={rule.condition}
                onChange={(e) => updateRule(idx, 'condition', e.target.value)}
                className={styles.planListInput}
              />
            </div>
            <div className={styles.debtField}>
              <span className={styles.debtLabel}>{isEn ? 'Action' : 'Action'}</span>
              <input
                type="text"
                value={rule.action}
                onChange={(e) => updateRule(idx, 'action', e.target.value)}
                className={styles.planListInput}
              />
            </div>
          </div>
        ))}
      </div>

      {renderEditableList('handoverCriteria', labels.handoverLabel)}
      {renderEditableList('missingInformation', labels.missingLabel)}

      <div className={styles.actionsBar}>
        <button
          type="button"
          className={`${styles.actionButton} ${copyStatus === 'copied' ? styles.actionButtonSuccess : ''}`}
          onClick={handleCopy}
        >
          {copyStatus === 'copied' ? <Check size={16} /> : <Copy size={16} />}
          {copyStatus === 'copied' ? labels.copied : labels.copyConfluence}
        </button>
        <button
          type="button"
          className={`${styles.actionButton} ${mdStatus === 'downloaded' ? styles.actionButtonSuccess : ''}`}
          onClick={handleDownload}
        >
          {mdStatus === 'downloaded' ? <Check size={16} /> : <Download size={16} />}
          {mdStatus === 'downloaded' ? labels.downloaded : labels.downloadMd}
        </button>
        <Link
          href={`/${lang}/rendez-vous`}
          className={`${styles.actionButton} ${styles.actionButtonSecondary}`}
          onClick={handleHumanReview}
        >
          <UserCheck size={16} />
          {labels.humanReview}
        </Link>
      </div>
    </div>
  );
}
